import type { Location, UpdateLocationRequest } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../../components/generic/Modal';
import { useUpdateLocationMutation } from '../../../graphql/hooks/useUpdateLocationMutation';
import { useAppContext } from '../../../hooks/useAppContext';
import { zodResolver } from '../../../utils/zod';
import { LocationForm } from './LocationForm';

type FormValues = UpdateLocationRequest;

const schema: z.ZodSchema<FormValues> = z.object({
  name: z.string(),
  color: z.string(),
});

interface Props {
  location: Location;
}

export const LocationUpdateModal = createModal<Props>((props) => {
  const { location, modal } = props;
  const { hide, remove, visible } = modal;
  const { errorToast, successToast, organization } = useAppContext();

  const initialValues: FormValues = {
    name: location.name,
    color: location.color,
  };

  const methods = useForm<FormValues>({
    defaultValues: initialValues,
    mode: 'all',
    resolver: zodResolver(schema),
  });

  const mutation = useUpdateLocationMutation();

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      await mutation
        .mutateAsync({
          organizationId: organization.id,
          locationId: location.id,
          request: values,
        })
        .then(() => {
          hide();
          successToast('Location updated successfully');
        })
        .catch((err) => {
          errorToast(`Error updating location: ${err.message}`);
        });
    },
    [mutation, organization.id, location.id, hide, successToast, errorToast],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Edit a location'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>
                <LocationForm />
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Save</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
